import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

import { SingleShipmentQuoteFields } from "components/QuoteFields";
import { getSubmitQuoteBlockMessage } from "utils/quotes";

import { QuotesTeammates } from "../QuotesTeammates";
import { QuotesOnlyShipmentForm } from "./components/QuotesOnlyShipmentForm";

import type { SubmitQuoteFormShipment } from "components/QuoteFields/types";

import type { SubmitQuoteFormState } from "./types";

interface Props {
  onValueChanged: <T>(key: keyof SubmitQuoteFormState, value: T) => void;
  enableSubmit: boolean;
  onSubmit: VoidFunction;
  error: string | null;
  loading: boolean;
  quoteSubmitted: boolean;
  canSubmitQuote: boolean;
  submitBlockReason: SubmitQuoteBlockReason | null;
  formState: SubmitQuoteFormState;
  quoteRefetchLoading: boolean;
  onPopulateShipmentRequirements: VoidFunction;
  shipment: SubmitQuoteFormShipment;
  carrierTeammatesOpened: boolean;
  closeCarrierTeammates: VoidFunction;
  openCarrierTeammates: VoidFunction;
  secondaryAction?: React.ReactNode;
}

export function SubmitQuoteForm(props: Props) {
  const {
    onValueChanged,
    enableSubmit,
    onSubmit,
    error,
    loading,
    quoteSubmitted,
    canSubmitQuote,
    submitBlockReason,
    formState,
    quoteRefetchLoading,
    onPopulateShipmentRequirements,
    shipment,
    carrierTeammatesOpened,
    closeCarrierTeammates,
    openCarrierTeammates,
    secondaryAction,
  } = props;

  const onCurrencyChanged = (newValue: string) => {
    onValueChanged<string>("currency", newValue);
  };

  if (!canSubmitQuote) {
    return (
      <Box pt="1rem">
        <Typography variant="body1">
          {submitBlockReason != null &&
            getSubmitQuoteBlockMessage(submitBlockReason)}
        </Typography>
      </Box>
    );
  }

  if (quoteRefetchLoading) {
    return (
      <Stack direction="row" justifyContent="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (shipment.info.type === "QUOTES_ONLY" && canSubmitQuote) {
    return (
      <QuotesOnlyShipmentForm
        onValueChanged={onValueChanged}
        onCurrencyChanged={onCurrencyChanged}
        enableSubmit={enableSubmit}
        onSubmit={onSubmit}
        error={error}
        loading={loading}
        quoteSubmitted={quoteSubmitted}
        canSubmitQuote={canSubmitQuote}
        formState={formState}
        quoteRefetchLoading={quoteRefetchLoading}
        carrierTeammatesOpened={carrierTeammatesOpened}
        closeCarrierTeammates={closeCarrierTeammates}
        openCarrierTeammates={openCarrierTeammates}
        shipment={shipment}
        secondaryAction={secondaryAction}
      />
    );
  }

  return (
    <Box pt="1rem">
      {shipment.teammate_quotes?.length > 0 && (
        <Alert severity="info">
          <Stack alignItems="flex-start">
            <Typography>
              Someone from your company has already quoted this load
            </Typography>
            <Button color="primary" onClick={openCarrierTeammates}>
              Click to see who has submitted quotes
            </Button>
          </Stack>
        </Alert>
      )}

      <SingleShipmentQuoteFields
        shipment={shipment}
        formState={formState}
        onValueChanged={onValueChanged}
        audience="carrier"
        onPopulateShipmentRequirements={onPopulateShipmentRequirements}
        loading={loading}
        onSubmit={onSubmit}
        submitLabel="Submit Quote"
        enableSubmit={enableSubmit}
        secondaryAction={secondaryAction}
        messages={
          <Stack direction="column" spacing={1}>
            {error != null && !loading && (
              <Typography color="red">{error}</Typography>
            )}
            {quoteSubmitted && (
              <Typography color="green">
                Quote submitted successfully.{" "}
              </Typography>
            )}
          </Stack>
        }
      />

      <QuotesTeammates
        carrierTeammatesOpened={carrierTeammatesOpened}
        closeCarrierTeammates={closeCarrierTeammates}
        teammates={shipment.teammate_quotes}
      />
    </Box>
  );
}
